import type { Services } from '~/services/types'
import type { StorageModules } from '~/storage/types'
import type {
    Invoice,
    BankTransaction,
    DocumentTransactionAssociation,
} from '~/storage/types'
import type { GoogleSheetsValueRange } from '~common/google-api/types'
import { formatTimestampForDisplay } from '~common/utils/date-transformation/date-transformations'
import type { DateFormat } from '~common/utils/date-transformation/types'
import type { DateRange } from 'react-day-picker'
import { getAllCategories } from '~/features/transactions/services/categories'
import { getBookingCategoriesFromPlugin } from '~/features/plugins/utils'

export interface ExportServiceDeps {
    services: Services
    storageModules: StorageModules
}

export interface ExportInvoicesOptions {
    columns: string[]
    dateRange?: DateRange
}

export interface ExportTransactionsOptions {
    columns: string[]
    dateRange?: DateRange
}

export class ExportService {
    constructor(private readonly deps: ExportServiceDeps) {}

    async exportInvoicesToGoogleSheets(
        options: ExportInvoicesOptions,
    ): Promise<{
        spreadsheetId: string
        spreadsheetUrl: string
    }> {
        let invoices =
            await this.deps.storageModules.emailInvoice.getAllInvoices()

        if (invoices.length === 0) {
            throw new Error('No invoices found to export')
        }

        invoices = this.filterByDateRange(
            invoices,
            (inv) => inv.metadata.date,
            options.dateRange,
        )

        if (invoices.length === 0) {
            throw new Error('No invoices found in the selected date range')
        }

        const user = await this.deps.storageModules.auth.getCurrentUser()
        if (!user?.googleDriveFolderHierarchy) {
            throw new Error('User Google Drive folder hierarchy not found')
        }

        const hierarchyMap = user.googleDriveFolderHierarchy
        const accountEmail = Object.keys(hierarchyMap)[0]
        if (!accountEmail) {
            throw new Error('No Google account found in folder hierarchy')
        }

        const hierarchy = hierarchyMap[accountEmail]
        if (!hierarchy?.exports?.folderId) {
            throw new Error('User Google Drive folder hierarchy not found')
        }

        const exportsFolderId = hierarchy.exports.folderId

        const timestamp = new Date().toISOString().split('T')[0]
        const spreadsheetTitle = `invoices_${timestamp}`

        const spreadsheet =
            await this.deps.services.googleApi.createGoogleSheetsSpreadsheet({
                title: spreadsheetTitle,
            })

        await this.deps.services.googleApi.moveFileToFolder(
            spreadsheet.spreadsheetId,
            exportsFolderId,
        )

        const dateFormat = user.settings?.dateFormat || 'MM/DD/YYYY'
        const data = this.formatInvoicesForExport(
            invoices,
            dateFormat,
            options.columns,
        )

        await this.deps.services.googleApi.updateGoogleSheetsValues(
            spreadsheet.spreadsheetId,
            'Sheet1!A1',
            data,
        )

        const updatedHierarchy = {
            ...hierarchyMap,
            [accountEmail]: {
                ...hierarchy,
                exports: {
                    ...hierarchy.exports,
                    files: [
                        ...hierarchy.exports.files,
                        {
                            title: spreadsheetTitle,
                            fileId: spreadsheet.spreadsheetId,
                            createdAt: new Date().toISOString(),
                        },
                    ],
                },
            },
        }

        await this.deps.storageModules.auth.updateUser(user.id, {
            googleDriveFolderHierarchy: updatedHierarchy,
        })

        return {
            spreadsheetId: spreadsheet.spreadsheetId,
            spreadsheetUrl: spreadsheet.spreadsheetUrl,
        }
    }

    async exportTransactionsToGoogleSheets(
        options: ExportTransactionsOptions,
    ): Promise<{
        spreadsheetId: string
        spreadsheetUrl: string
    }> {
        let transactions =
            await this.deps.storageModules.bankTransaction.getAllTransactions()

        if (transactions.length === 0) {
            throw new Error('No transactions found to export')
        }

        transactions = this.filterByDateRange(
            transactions,
            (tx) => tx.date,
            options.dateRange ?? undefined,
        )

        if (transactions.length === 0) {
            throw new Error('No transactions found in the selected date range')
        }

        const user = await this.deps.storageModules.auth.getCurrentUser()
        if (!user?.googleDriveFolderHierarchy) {
            throw new Error('User Google Drive folder hierarchy not found')
        }

        const hierarchyMap = user.googleDriveFolderHierarchy
        const accountEmail = Object.keys(hierarchyMap)[0]
        if (!accountEmail) {
            throw new Error('No Google account found in folder hierarchy')
        }

        const hierarchy = hierarchyMap[accountEmail]
        if (!hierarchy?.exports?.folderId) {
            throw new Error('User Google Drive folder hierarchy not found')
        }

        const exportsFolderId = hierarchy.exports.folderId

        const timestamp = new Date().toISOString().split('T')[0]
        const spreadsheetTitle = `transactions_${timestamp}`

        const spreadsheet =
            await this.deps.services.googleApi.createGoogleSheetsSpreadsheet({
                title: spreadsheetTitle,
            })

        await this.deps.services.googleApi.moveFileToFolder(
            spreadsheet.spreadsheetId,
            exportsFolderId,
        )

        const dateFormat = user.settings?.dateFormat || 'MM/DD/YYYY'
        const data = this.formatTransactionsForExport(
            transactions,
            dateFormat,
            options.columns,
        )

        await this.deps.services.googleApi.updateGoogleSheetsValues(
            spreadsheet.spreadsheetId,
            'Sheet1!A1',
            data,
        )

        const updatedHierarchy = {
            ...hierarchyMap,
            [accountEmail]: {
                ...hierarchy,
                exports: {
                    ...hierarchy.exports,
                    files: [
                        ...hierarchy.exports.files,
                        {
                            title: spreadsheetTitle,
                            fileId: spreadsheet.spreadsheetId,
                            createdAt: new Date().toISOString(),
                        },
                    ],
                },
            },
        }

        await this.deps.storageModules.auth.updateUser(user.id, {
            googleDriveFolderHierarchy: updatedHierarchy,
        })

        return {
            spreadsheetId: spreadsheet.spreadsheetId,
            spreadsheetUrl: spreadsheet.spreadsheetUrl,
        }
    }

    private filterByDateRange<T>(
        items: T[],
        getDate: (item: T) => Date | number,
        dateRange?: DateRange,
    ): T[] {
        if (!dateRange?.from && !dateRange?.to) {
            return items
        }
        let fromDate: number | undefined = 0
        let toDate: number | undefined = Date.now()

        if (dateRange?.from) {
            const fromDateObj = new Date(dateRange.from)
            fromDate = Date.UTC(
                fromDateObj.getFullYear(),
                fromDateObj.getMonth(),
                fromDateObj.getDate(),
                0,
                0,
                0,
                0,
            )
        }

        if (dateRange?.to) {
            const toDateObj = new Date(dateRange.to)
            toDate = Date.UTC(
                toDateObj.getFullYear(),
                toDateObj.getMonth(),
                toDateObj.getDate(),
                23,
                59,
                59,
                999,
            )
        }

        return items.filter((item) => {
            const itemDateOrTimestamp = getDate(item)
            if (typeof itemDateOrTimestamp !== 'number') {
                return false
            }

            return (
                itemDateOrTimestamp >= fromDate && itemDateOrTimestamp <= toDate
            )
        })
    }

    private formatInvoicesForExport(
        invoices: Invoice[],
        dateFormat: DateFormat,
        selectedColumns: string[],
    ): GoogleSheetsValueRange['values'] {
        if (invoices.length === 0) return []

        const columnMap: Record<string, (invoice: Invoice) => string> = {
            id: (inv) => inv.id,
            date: (inv) =>
                inv.metadata.date
                    ? formatTimestampForDisplay(inv.metadata.date, dateFormat)
                    : '',
            amount: (inv) => String(inv.metadata.amount || ''),
            netAmount: (inv) => String(inv.metadata.netAmount || ''),
            currency: (inv) => inv.metadata.currency || '',
            vatRate: (inv) => String(inv.metadata.vatRate || ''),
            vatAmount: (inv) => String(inv.metadata.vatAmount || ''),
            invoiceId: (inv) => inv.metadata.invoiceId || '',
            creditCard: (inv) => inv.metadata.creditCard || '',
            vendorName: (inv) => inv.metadata.vendorName || '',
            vendorAddress: (inv) => inv.metadata.vendorAddress || '',
            vendorCountry: (inv) => inv.metadata.vendorCountry || '',
            vendorIbans: (inv) => inv.metadata.vendorIbans?.join(', ') || '',
            vendorEmailAddresses: (inv) =>
                inv.metadata.vendorEmailAddresses?.join(', ') || '',
            reason: (inv) => inv.metadata.reason || '',
            googleDriveFileId: (inv) => inv.googleDriveFileId,
            googleDriveFileLink: (inv) =>
                `https://drive.google.com/file/d/${inv.googleDriveFileId}`,
            sourceType: (inv) => inv.sourceType,
            createdAt: (inv) => new Date(inv.createdAt).toLocaleDateString(),
            updatedAt: (inv) => new Date(inv.updatedAt).toLocaleDateString(),
        }

        const columnLabels: Record<string, string> = {
            id: 'ID',
            date: 'Date',
            amount: 'Amount',
            netAmount: 'Net Amount',
            currency: 'Currency',
            vatRate: 'VAT Rate',
            vatAmount: 'VAT Amount',
            invoiceId: 'Invoice ID',
            creditCard: 'Credit Card',
            vendorName: 'Vendor Name',
            vendorAddress: 'Vendor Address',
            vendorCountry: 'Vendor Country',
            vendorIbans: 'Vendor IBANs',
            vendorEmailAddresses: 'Vendor Email Addresses',
            reason: 'Reason',
            googleDriveFileId: 'Google Drive File ID',
            googleDriveFileLink: 'Google Drive File Link',
            sourceType: 'Source Type',
            createdAt: 'Created At',
            updatedAt: 'Updated At',
        }

        const headers = selectedColumns.map((col) => columnLabels[col] || col)
        const rows: GoogleSheetsValueRange['values'] = [headers]

        for (const invoice of invoices) {
            const row = selectedColumns.map((col) => {
                const formatter = columnMap[col]
                return formatter ? formatter(invoice) : ''
            })
            rows.push(row)
        }

        return rows
    }

    private formatTransactionsForExport(
        transactions: BankTransaction[],
        dateFormat: DateFormat,
        selectedColumns: string[],
    ): GoogleSheetsValueRange['values'] {
        if (transactions.length === 0) return []

        const cache = this.deps.services.cache
        const matchesByTransaction = cache.getKey('matchesByTransaction') || {}
        const documentTransactionAssociations =
            cache.getKey('documentTransactionAssociations') ||
            ({} as Record<string, DocumentTransactionAssociation>)
        const invoices =
            cache.getKey('invoices') || ({} as Record<string, Invoice>)
        const settings = cache.getKey('settings')
        const bookingCategories = getBookingCategoriesFromPlugin(settings)
        const categories = getAllCategories(bookingCategories)

        const getMatchingInvoiceUrl = (tx: BankTransaction): string => {
            const assocIds = matchesByTransaction[tx.id] || []
            if (assocIds.length === 0) return ''

            const associations = assocIds
                .map((id) => documentTransactionAssociations[id])
                .filter((assoc): assoc is DocumentTransactionAssociation =>
                    Boolean(assoc),
                )
                .sort((a, b) => {
                    if (
                        a.method === 'user_confirmed' &&
                        b.method !== 'user_confirmed'
                    )
                        return -1
                    if (
                        b.method === 'user_confirmed' &&
                        a.method !== 'user_confirmed'
                    )
                        return 1
                    return (b.confidence ?? 0) - (a.confidence ?? 0)
                })

            if (associations.length > 0) {
                const invoiceId = associations[0].documentId
                const invoice = invoices[invoiceId]
                if (invoice?.googleDriveFileId) {
                    return `https://drive.google.com/file/d/${invoice.googleDriveFileId}`
                }
            }

            return ''
        }

        const columnMap: Record<string, (tx: BankTransaction) => string> = {
            id: (tx) => tx.id,
            accountIban: (tx) => tx.accountIban,
            vendorId: (tx) => tx.vendorId || '',
            vendorName: (tx) => tx.vendorName,
            counterpartyIban: (tx) => tx.counterpartyIban,
            date: (tx) => formatTimestampForDisplay(tx.date, dateFormat),
            amount: (tx) => String(tx.amount),
            currency: (tx) => tx.currency,
            referenceText: (tx) => tx.referenceText,
            note: (tx) => tx.note,
            categoryId: (tx) => String(tx.category || ''),
            categoryLabel: (tx) =>
                tx.category ? categories[String(tx.category)]?.label || '' : '',
            matchingInvoiceUrl: (tx) => getMatchingInvoiceUrl(tx),
            status: (tx) => tx.status,
            createdAt: (tx) => new Date(tx.createdAt).toLocaleDateString(),
            updatedAt: (tx) => new Date(tx.updatedAt).toLocaleDateString(),
        }

        const columnLabels: Record<string, string> = {
            id: 'ID',
            accountIban: 'Account IBAN',
            vendorId: 'Vendor ID',
            vendorName: 'Vendor Name',
            counterpartyIban: 'Counterparty IBAN',
            date: 'Date',
            amount: 'Amount',
            currency: 'Currency',
            referenceText: 'Reference Text',
            note: 'Note',
            categoryId: 'Category ID',
            categoryLabel: 'Category Label',
            matchingInvoiceUrl: 'Matching Invoice URL',
            status: 'Status',
            createdAt: 'Created At',
            updatedAt: 'Updated At',
        }

        const headers = selectedColumns.map((col) => columnLabels[col] || col)
        const rows: GoogleSheetsValueRange['values'] = [headers]

        for (const transaction of transactions) {
            const row = selectedColumns.map((col) => {
                const formatter = columnMap[col]
                return formatter ? formatter(transaction) : ''
            })
            rows.push(row)
        }

        return rows
    }
}
